import { UserContext } from "@contexts/UserContext";
import type { UserLocalStorage } from "@typings/entities/users";
import type { ROLES } from "@typings/entities/users/enums";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { useState, useMemo, useCallback, useEffect } from "react";

function isTokenExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return !exp || exp < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
}

function getRoleFromToken(
  token: string,
): (typeof ROLES)[keyof typeof ROLES] | null {
  try {
    const decoded = jwtDecode<{ role?: string }>(token);
    return (decoded.role as (typeof ROLES)[keyof typeof ROLES]) || null;
  } catch {
    return null;
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [isCheckingUser, setCheckingUser] = useState(true);
  const [user, setUser] = useState<UserLocalStorage | null>(null);
  const [role, setRole] = useState<(typeof ROLES)[keyof typeof ROLES] | null>(
    null,
  );

  const saveUser = useCallback(
    async (newUser: UserLocalStorage | null): Promise<void> => {
      if (newUser === null) {
        localStorage.removeItem("user");
        setUser(null);
        setRole(null);
      } else {
        localStorage.setItem("user", JSON.stringify(newUser));
        setUser(newUser);

        const token = Cookies.get("x_access_token");
        if (token && !isTokenExpired(token)) {
          const tokenRole = getRoleFromToken(token);
          setRole(tokenRole);
        }
      }
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    setUser(null);
    setRole(null);
    localStorage.removeItem("user");
    Cookies.remove("x_access_token");
  }, []);

  const checkSession = useCallback(async () => {
    setCheckingUser(true);

    try {
      const token = Cookies.get("x_access_token");

      if (!token || isTokenExpired(token)) {
        await signOut();
        return;
      }

      const tokenRole = getRoleFromToken(token);
      if (!tokenRole) {
        await signOut();
        return;
      }
      setRole(tokenRole);

      const userData = localStorage.getItem("user");
      if (!userData) {
        await signOut();
        return;
      }

      const parsedUser = JSON.parse(userData) as UserLocalStorage;
      setUser(parsedUser);
    } catch {
      await signOut();
    } finally {
      setCheckingUser(false);
    }
  }, [signOut]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const value = useMemo(
    () => ({
      user,
      role,
      checkSession,
      signOut,
      saveUser,
      isCheckingUser,
      setCheckingUser,
    }),
    [user, role, isCheckingUser, checkSession, signOut, saveUser],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
